import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AnalyticsEventName, getClientBrowserParameters, sendShopifyAnalytics } from "@shopify/hydrogen-react";
import { FLAVOURS } from "@/data/content";
import { findSixPack, useShop } from "@/lib/shop";

// Reports visits on this site to Shopify Analytics (Sessions / Online store visitors)
const SHOP_ID = process.env.REACT_APP_SHOPIFY_SHOP_ID;
const SHOP_DOMAIN = process.env.REACT_APP_SHOPIFY_ANALYTICS_DOMAIN;
// only count real visitors on the live site, never the preview (localStorage sb-analytics-debug=1 forces it on for QA)
const debugOn = () => { try { return localStorage.getItem("sb-analytics-debug") === "1"; } catch { return false; } };
const isLive = () => window.location.hostname.endsWith("swolebears.ca") || debugOn();

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const readCookie = (k) => document.cookie.match(new RegExp(`\\b${k}=([^;]+)`))?.[1];
const cookieDomain = () => (location.hostname.endsWith("swolebears.ca") ? "; domain=.swolebears.ca" : "");
const setCookie = (k, v, seconds) => { document.cookie = `${k}=${v}; max-age=${seconds}; path=/; SameSite=Lax${cookieDomain()}`; };

// Shopify's visitor (1 year) + session (30 min, refreshed on activity) tokens, shared with checkout on shop.swolebears.ca
const ensureTokens = () => {
  setCookie("_shopify_y", readCookie("_shopify_y") || uuid(), 60 * 60 * 24 * 365);
  setCookie("_shopify_s", readCookie("_shopify_s") || uuid(), 60 * 30);
};

const base = () => ({ shopId: SHOP_ID, currency: "CAD", acceptedLanguage: "EN", hasUserConsent: true, shopifySalesChannel: "headless" });

const toProduct = (p, qty = 1) => ({
  productGid: `gid://shopify/Product/${p.id}`, variantGid: `gid://shopify/ProductVariant/${p.variant_id}`,
  name: p.title, price: String(p.price), brand: "Swole Bears", quantity: qty,
});

const pageInfo = (path, products) => {
  if (path === "/") return { pageType: "index" };
  if (path === "/shop") return { pageType: "collection" };
  if (path.startsWith("/flavours/")) {
    const f = FLAVOURS.find((x) => x.key === path.split("/")[2]);
    const p = f && findSixPack(products, f);
    return p ? { pageType: "product", resourceId: `gid://shopify/Product/${p.id}`, products: [toProduct(p)], totalValue: p.price } : { pageType: "product" };
  }
  return { pageType: "page" };
};

export const trackAddToCart = (product, qty = 1) => {
  if (!isLive() || !SHOP_ID || !product?.id) return;
  ensureTokens();
  sendShopifyAnalytics({ eventName: AnalyticsEventName.ADD_TO_CART,
    payload: { ...getClientBrowserParameters(), ...base(), products: [toProduct(product, qty)], totalValue: product.price * qty } }, SHOP_DOMAIN);
};

export const ShopifyAnalytics = () => {
  const { pathname } = useLocation();
  const { products, status } = useShop();
  const ready = status !== "loading";
  useEffect(() => {
    if (!isLive() || !SHOP_ID || !ready) return undefined;
    // wait a beat so the page title for this route is set
    const t = setTimeout(() => {
      ensureTokens();
      sendShopifyAnalytics({ eventName: AnalyticsEventName.PAGE_VIEW,
        payload: { ...getClientBrowserParameters(), ...base(), ...pageInfo(pathname, products) } }, SHOP_DOMAIN);
    }, 300);
    return () => clearTimeout(t);
    // products intentionally omitted: one page view per route change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, ready]);
  return null;
};
